/*
  <nb-rail>: progressive enhancement for the block library rails.
  The track scrolls natively with CSS scroll-snap; this element only wires the arrow buttons,
  disables them at the ends, and announces the position in a live region.
*/
if (!customElements.get('nb-rail')) {
  class NbRail extends HTMLElement {
    constructor() {
      super();
      this.onPrev = this.onPrev.bind(this);
      this.onNext = this.onNext.bind(this);
      this.onScroll = this.throttle(this.update.bind(this), 120);
      this.onResize = this.throttle(this.update.bind(this), 200);
    }

    connectedCallback() {
      this.track = this.querySelector('[data-rail-track]');
      this.prev = this.querySelector('[data-rail-prev]');
      this.next = this.querySelector('[data-rail-next]');
      this.status = this.querySelector('[data-rail-status]');
      if (!this.track) return;
      if (this.prev) this.prev.addEventListener('click', this.onPrev);
      if (this.next) this.next.addEventListener('click', this.onNext);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      // Section CSS loads non-blocking, so the track may not overflow yet when this runs.
      // A ResizeObserver re-evaluates the arrows once layout settles (and on viewport changes).
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(this.onResize);
        this.observer.observe(this.track);
      } else {
        window.addEventListener('resize', this.onResize);
      }
      this.update();
    }

    disconnectedCallback() {
      if (this.prev) this.prev.removeEventListener('click', this.onPrev);
      if (this.next) this.next.removeEventListener('click', this.onNext);
      if (this.track) this.track.removeEventListener('scroll', this.onScroll);
      if (this.observer) this.observer.disconnect();
      window.removeEventListener('resize', this.onResize);
    }

    throttle(fn, wait) {
      let last = 0;
      let timer = null;
      return () => {
        const now = Date.now();
        const remaining = wait - (now - last);
        if (remaining <= 0) {
          clearTimeout(timer);
          timer = null;
          last = now;
          fn();
        } else if (!timer) {
          timer = setTimeout(() => {
            timer = null;
            last = Date.now();
            fn();
          }, remaining);
        }
      };
    }

    reduceMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    step() {
      const first = this.track.children[0];
      if (!first) return this.track.clientWidth;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return first.getBoundingClientRect().width + gap;
    }

    scrollByStep(direction) {
      this.track.scrollBy({ left: direction * this.step(), behavior: this.reduceMotion() ? 'auto' : 'smooth' });
    }

    onPrev() { this.scrollByStep(-1); }
    onNext() { this.scrollByStep(1); }

    update() {
      if (!this.track) return;
      const max = this.track.scrollWidth - this.track.clientWidth - 1;
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 1;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max;
      if (this.status) {
        const total = this.track.children.length;
        const index = Math.min(Math.round(this.track.scrollLeft / this.step()) + 1, total);
        const template = this.status.dataset.template || '';
        this.status.textContent = template.replace('[current]', index).replace('[total]', total);
      }
    }
  }
  customElements.define('nb-rail', NbRail);
}
